import { type FormEvent, useEffect, useState } from 'react'

import {
  Button,
  CopyButton,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  TextField,
} from '@/components/ui'
import { useToast } from '@/hooks/useToast'
import {
  useInvitations,
  useMembers,
  useMembersMutations,
  useMembersStore,
} from '@/modules/account'
import { useDialogActions, useDialogIsOpen, useForm } from '@/stores'
import { getInvitationPath, getUrl } from '@/utils'

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

const dialogId = 'invite-member'

export const InviteMemberDialog = () => {
  const open = useDialogIsOpen(dialogId)
  const { closeDialog, openDialog } = useDialogActions()
  const { inviteMember } = useMembersMutations()
  const members = useMembers()
  const invitations = useInvitations()
  const mutating = useMembersStore((s) => s.mutating)
  const { errorToast } = useToast()

  const { data, errors, setFieldValue, setFieldError } = useForm()
  const [inviteUrl, setInviteUrl] = useState('')

  useEffect(() => {
    if (!open) return
    setFieldError('email', '')
    setFieldValue('email', '')
    setInviteUrl('')
  }, [open])

  const validate = (email: string) => {
    if (!EMAIL_REGEX.test(email.trim())) {
      setFieldError('email', 'Enter a valid email address')
      return false
    }

    if (members.find((member) => member.email === email)) {
      setFieldError('email', 'Already a member')
      return false
    }

    const existingInvitation = invitations.find(
      (invitation) => invitation.email === email,
    )
    if (
      existingInvitation?.status === 'pending' &&
      !existingInvitation.hasExpired
    ) {
      setFieldError('email', 'An invitation for this email is already pending')
      return false
    }

    setFieldError('email', '')
    return true
  }

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault()
    const { email } = data as { email: string }
    if (!validate(email)) return
    const sanitizedEmail = email.trim().toLowerCase()

    inviteMember(sanitizedEmail)
      .then((invitation) => setInviteUrl(getUrl(getInvitationPath(invitation))))
      .catch((error) => errorToast('Failed to send invitation', error))
  }

  return (
    <Dialog open={open} onOpenChange={(open) => !open && closeDialog()}>
      <DialogTrigger asChild onClick={() => openDialog(InviteMemberDialog.id)}>
        <Button size="sm">Invite Member</Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Invite a team member</DialogTitle>
          <DialogDescription>
            Send an invitation link to add someone to your account.
          </DialogDescription>
        </DialogHeader>

        {inviteUrl ? (
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              Invitation created for <strong>{data?.email}</strong>. Share this
              link:
            </p>
            <div className="flex items-center gap-2">
              <input
                readOnly
                value={inviteUrl}
                className="flex-1 rounded-md border px-3 py-2 text-sm bg-muted"
              />
              <CopyButton theme="outline" text={inviteUrl} />
            </div>
            <DialogFooter>
              <Button theme="outline" onClick={() => closeDialog(false)}>
                Done
              </Button>
            </DialogFooter>
          </div>
        ) : (
          <form onSubmit={onSubmit}>
            <TextField
              label="Email address"
              type="email"
              value={data?.email as string}
              onChange={(e) => setFieldValue('email', e.target.value)}
              error={errors?.email}
              placeholder="colleague@firm.com"
            />
            <DialogFooter className="mt-4">
              <Button
                theme="outline"
                type="button"
                onClick={() => closeDialog(false)}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                isLoading={mutating}
                loadingText="Sending..."
              >
                Send Invitation
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  )
}

InviteMemberDialog.id = dialogId
